// Please see: https://dev.to/0916dhkim/type-safe-usage-of-react-router-5c44
import { useNavigate, useLocation } from "react-router-dom";
import { Location } from "history";
import { LocationStates } from "../types";

export enum RoutePaths {
  redirectAuth = "/redirect/auth",
  testHomePage = "/test/home",
  sandbox = "/sandbox",
  openIRA = "/openIRA",
  any = "/:any",
  index = "/",
  mfa = "/mfa",
  accountDetails = "/accountDetails",
  beginTransferInitiation = "/beginTransferInitiation",
  changePassword = "/changePassword",
  createAccount = "/createAccount",
  destinationOptionsIntro = "/destinationOptionsIntro",
  destinationSelection = "/destinationSelection",
  destinationTransferOptions = "/destinationTransferOptions",
  destinationTransferOptionsWithSection = "/destinationTransferOptions/:section",
  emailVerification = "/emailVerification/:email",
  error = "/error",
  findYourOldAccounts = "/findYourOldAccounts",
  vendor = "/:vendorName",
  vendorCalculator = "/:vendorName/calculator",
  vendorSurvey = "/:vendorName/survey",
  welcome = "/welcome",
  welcomeWithVendor = "/welcome/:vendorName",
  welcomeVendorCalculator = "/welcome/:vendorName/calculator",
  welcomeVendorSurvey = "/welcome/:vendorName/survey",
  home = "/home",
  iraOptions = "/IRAOptions",
  iraPartner = "/IRAOptions/:iraPartner",
  findBestIRA = "/findBestIRA",
  connectDestination = "/connectDestination",
  connectOldAccount = "/connectOldAccount",
  login = "/login",
  onboarding = "/onboarding",
  openAndConnectIRADestination = "/openAndConnectIRADestination",
  resetPassword = "/resetPassword",
  passwordUpdated = "/passwordUpdated",
  settings = "/settings",
  transferActions = "/transferActions",
  transferCheck = "/transferCheck",
  transferDetails = "/transferDetails",
  transferHelp = "/transferHelp",
  transferIntoEmployersPlan = "/transferIntoEmployersPlan",
  transferInitiation = "/transferInitiation",
  transferOptions = "/transferOptions",
  transferStatusRedirect = "/transferStatus",
  transferStatus = "/transferStatus/:action",
  transferSteps = "/transferSteps",
  userDetails = "/userDetails",
  iraOpenForm = "/iraOpenForm",
  userFeedback = "/userFeedback",
  changelog = "/changelog",
  henryLandingPage = "/welcome/:vendor/henry",
  schedulePhoneCall = "/schedulePhoneCall",
  redirectCallback = "/redirect/callback",
  formResubmit = "/formResubmit",
}

type ExtractRouteParams<T> = string extends T
  ? Record<string, string>
  : // eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends `${infer _Start}:${infer Param}/${infer Rest}`
  ? { [k in Param | keyof ExtractRouteParams<Rest>]: string }
  : // eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends `${infer _Start}:${infer Param}`
  ? { [k in Param]: string }
  : undefined;
export type PathParams<P extends RoutePaths> = ExtractRouteParams<P>;

export const useTypedLocation = useLocation as () => Location & {
  state: LocationStates;
};
export const useTypedNavigate = useNavigate as () => {
  (
    to: RoutePaths,
    options?: {
      replace?: boolean;
      state?: LocationStates;
    }
  ): void;
  (delta: number): void;
};

/**
 * Build a url with a path and its parameters.
 * @example
 * buildUrl(
 *   '/a/:first/:last',
 *   { first: 'p', last: 'q' },
 * ) // returns '/a/p/q'
 * @param path target path.
 * @param params parameters.
 */
export const route = <P extends RoutePaths>(path: P, params?: PathParams<P> | false): RoutePaths => {
  if (params === false) return path;
  let ret = `${path}`;
  const paramObj: { [i: string]: string } = (params || {}) as any;
  Object.keys(paramObj).forEach((key) => {
    ret = ret.replace(`:${key}`, paramObj[key]);
  });
  return ret as RoutePaths;
};
